"use client";
import {
  CategoryStyleTypes,
  ICategory,
  IProduct_Category_Config_Customize,
} from "@/api/AppApi/services/home/interfaces/ICategory";
import { Link } from "@/app/components/lib/navigation/Link";
import NextImage from "../../lib/NextImage";
import { useRef } from "react";
import { useGetCategoryAdvanceStyle } from "./helpers/useGetCategoryAdvanceStyle";
import useIsClient from "@/hooks/useIsClient";

interface Props extends ICategory {
  categoryType: CategoryStyleTypes;
  customize: IProduct_Category_Config_Customize;
  showCategoryImage: boolean;
}
/**
 * Config values may arrive as a number or a string; CSS vars need a unit.
 *
 * A bare numeric STRING ("140") is the case this exists for — returning it
 * unchanged produced `--cat-w-m: 140`, which is invalid as a `width` and makes
 * the whole declaration fall back to `auto`, silently undoing the first-paint
 * sizing. A string that already carries a unit ("8rem", "50%") is passed through.
 */
const px = (v?: number | string) => {
  if (v === undefined || v === null || v === "") return undefined;
  if (typeof v === "number") return `${v}px`;
  const s = String(v).trim();
  return /^-?\d+(\.\d+)?$/.test(s) ? `${s}px` : s;
};

const Category: React.FC<Props> = ({
  id,
  categoryType,
  icon,
  name,
  customize,
  showCategoryImage,
  slug,
}) => {
  const catType = categoryType ?? CategoryStyleTypes.circle_icon;
  let cn = "";
  switch (catType) {
    case CategoryStyleTypes.square:
      cn = "category-square";
      break;
    case CategoryStyleTypes.square_primary:
      cn = "category-square primary";
      break;
    case CategoryStyleTypes.square_primary_title:
      cn = "category-square-colored-title";
      break;
    case CategoryStyleTypes.circle_img:
      cn = "category-circle-img";
      break;
    default:
      cn = "category-circle-img";
  }

  const lebleRef = useRef<HTMLDivElement>(null);
  const { advance_mode, width, width_mobile, height, height_mobile } =
    customize ?? {};
  const { advanceFontSize, advanceStyle, advancefullSize } =
    useGetCategoryAdvanceStyle(customize, lebleRef);
  const viewIcon = showCategoryImage && icon && icon !== "";
  const noImageClasses = " w-100 align-items-center";
  const isClient = useIsClient();
  const advanceMode = advance_mode && isClient;

  // Reserve the advance-mode footprint from the FIRST paint.
  //
  // `advanceStyle` carries the tenant's configured width/height, but it was
  // applied only after `useIsClient()` flipped — so the tile painted at the CSS
  // default (70px), then jumped to its real size (140px). That single reflow was
  // the largest layout shift on the home page (0.346 of a 0.526 CLS).
  //
  // The sizes come from server data, so they can be emitted immediately. They go
  // out as CSS custom properties rather than inline width/height because the
  // mobile/desktop choice must be made by a media query: `useIsMobile()` reports
  // 0 width until mount, so picking in JS would just move the shift to desktop.
  const advanceVars = advance_mode
    ? ({
        "--cat-w": px(width),
        "--cat-w-m": px(width_mobile),
        "--cat-h": px(height),
        "--cat-h-m": px(height_mobile),
      } as React.CSSProperties)
    : undefined;

  return (
    <Link prefetch={false} href={`/categories/${slug || id}`}>
      <div
        className={`${cn} ${viewIcon ? "" : "justify-content-center"} ${
          advance_mode ? "cat-advance-size" : ""
        }`}
        style={{ ...advanceVars, ...(advanceMode ? advanceStyle : {}) }}
      >
        {viewIcon && (
          <div
            className="category-image-cover"
            style={advanceMode ? { ...advancefullSize } : {}}
          >
            <div
              className="category-image-container"
              style={advanceMode ? { width: "100%", height: "100%" } : {}}
            >
              <NextImage
                src={icon}
                alt="category"
                sizes="70px"
                objectFit="contain"
                style={{ backgroundColor: "#FFF" }}
              />
            </div>
          </div>
        )}
        <div
          ref={lebleRef}
          className={`category-label${advanceMode ? "-advance" : ""} ${
            !showCategoryImage ? noImageClasses : ""
          }`}
          style={advanceMode ? { fontSize: advanceFontSize } : {}}
        >
          {name}
        </div>
      </div>
    </Link>
  );
};

export default Category;
